/* eslint-disable camelcase */
import React, {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react"
import { observer } from "mobx-react-lite"
import {
  ActivityIndicator,
  Alert,
  AlertButton,
  Appearance,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleProp,
  ViewStyle,
  ImageStyle,
  TouchableOpacity,
  InteractionManager,
} from "react-native"
import {
  Button,
  JalaIcon,
  JalaLoading,
  Screen,
  SettingItem,
  Text,
  useAutoFeederPond,
  WalkthroughElement,
  AutoFeederPondDisconnected,
  InfoModal,
  OnboardAstoModal,
  AutoImage,
  ProgressDialog,
  AutoFeederCalibrationModal
} from "../../../../components"
import { useStores } from "../../../../models"
import { CompositeScreenProps, useIsFocused } from "@react-navigation/native"
import { color, nativeBaseTheme } from "../../../../theme"
import {
  Divider, Icon, Modal,
  Toast, useDisclose, View
} from "native-base"
import { goBack, navigate } from "../../../../navigators"
import numeral from "numeral"
import moment from "moment"
import { translate, TxKeyPath } from "../../../../i18n"
import _ from "lodash"
import { AutoFeederPond } from "../../../../database"
import { delay } from "../../../../utils/delay"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { activateKeepAwake, deactivateKeepAwake } from "@sayem314/react-native-keep-awake"
import { IViewProps } from "native-base/lib/typescript/components/primitives/View"

const autoFeederImage = require('../../farm/farm-auto-feeder-pond-list/asto.png')
const autoFeederDisabledImage = require('./auto-feeder-disabled.png')

const ROOT: ViewStyle = {
  flexGrow: 1,
  paddingBottom: 68
}

const TOP_BAR_CONTAINER: ViewStyle = {
  justifyContent: "center",
}

const ACTIVITY_BUTTON: ViewStyle = {
  paddingHorizontal: 16,
  paddingLeft: 0
}

const RIGHT_BUTTON: ViewStyle = {
  paddingLeft: 0,
  paddingRight: 16,
  ...Platform.select({ web: { paddingRight: 16 } })
}

const IMAGE_AUTO_FEEDER: StyleProp<ImageStyle> = {
  width: 150,
  height: 150,
  alignSelf: "flex-start",
  marginLeft: 4
}

const MODAL: ViewStyle = {
  margin: 0,
  width: '100%',
  justifyContent: 'flex-end',
  position: 'absolute',
  bottom: 0,
  flex: 1,
  backgroundColor: color.palette.white,
  paddingTop: 20,
  paddingBottom: 10
}

const CONNECTING_CARD = (borderColor, disabled): ViewStyle => ({
  flexDirection:"row",
  borderColor: disabled ? color.palette.gray400 : borderColor,
  borderWidth: 1,
  borderRadius: 10,
  paddingHorizontal: 16,
  paddingVertical: 10,
  marginHorizontal: 16,
  alignItems: "center",
  marginBottom: 16,
  backgroundColor: color.palette.white
})

const MODAL_BUTTON: ViewStyle = {
  width: "45%",
  borderRadius: 40,
  backgroundColor: color.palette.white
}

const MODAL_BUTTON_RIGHT: ViewStyle = {
  width: "45%",
  borderRadius: 40,
  backgroundColor: color.primary
}

const ACTIVITY_HEADER_CONTAINER: ViewStyle = {
  position: 'absolute',
  top: 0,
  paddingHorizontal: 16,
  paddingVertical: 8,
  backgroundColor: color.palette.orange300,
  alignItems: 'center',
  justifyContent: "space-between",
  flexDirection: "row",
  zIndex: 1
}

const ASTO_UPLOAD_IMAGE: ImageStyle = {
  width: 27,
  height: 28,
  resizeMode: "contain",
  marginRight: 4
}

const INDICATOR: ViewStyle = {
  marginLeft: 9
}

const BUTTON_CLOSE_CYCLE: ViewStyle = {
  borderRadius: 20,
  paddingHorizontal: 16
}

const ADVANCED_SETTING: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "row",
  padding: 12
}

const CLOSE_ICON: ViewStyle = {
  marginLeft: 5,
  paddingLeft: 8,
  width: "10%",
  alignItems: "flex-end"
}

const CALIBRATION_BUTTON: ViewStyle = {
  // width: '30%'
  alignSelf: "flex-start"
}

const BUTTON: ViewStyle = { paddingHorizontal: 16 }

interface FeedState {
  modified: boolean,
  originalTimesPerDay?: number,
  timesPerDay?: number,
  originalFeeds: Array<any>,
  feeds: Array<any>,
  originalFeedsPerDay: Array<any>,
}

interface FeedAction {
  type: "reset"|"update-feed"|"update-feed-per-day"|"set-modified"|"set-times-per-day",
  index?: number,
  payload?: any
}

const UploadWarning: FC = observer(function ActivityHeader() {
  const [ showHeader, setShowHeader ] = useState<boolean>(false)
  const { state: { modified } } = useContext(FeedStateContext)
  const { autoFeederPond: model } = useAutoFeederPond()

  useEffect(() => {
    if(modified) setShowHeader(true)
    else setShowHeader(false)
  }, [model.isConnected, modified])

  return (showHeader && <View height="35px" zIndex={1}>
    <View style={ACTIVITY_HEADER_CONTAINER}>
      <JalaIcon name="info-1" size={20} color={color.palette.gray800} />
      <Text tx="autoFeederPondDetailScreen.uploadWarning" color={color.palette.gray800} preset="caption2" marginLeft="8px" numberOfLines={1} />
    </View>
  </View>)
})

const UploadSuccess : FC<{ colorScheme?: "light" | "dark" }> = observer(function UploadButton(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
  } = props
  const img = require("./asto-upload-succeed.png")

  return (
    <View position="absolute" right={0} bottom={0} left={0}>
      <View
        marginX="20px"
        marginBottom="36px"
        padding="5px"
        borderRadius="10px"
        borderColor={color.palette.green500}
        backgroundColor={color.palette.white}
      >
        <View flexDirection="row" alignItems="center" justifyContent="space-between">
          <AutoImage source={img} style={ASTO_UPLOAD_IMAGE} />
          <View flex={1} paddingX="8px" marginY="6px">
            <Text preset="title4" tx="autoFeederPondDetailScreen.uploadSuccessTitle" colorScheme={colorScheme} />
            <Text preset="body3" tx="autoFeederPondDetailScreen.uploadSuccessSubtitle" colorScheme={colorScheme} numberOfLines={2} />
          </View>
        </View>
      </View>
    </View>
  )
})

const ModalCycleClosed: FC<{ colorScheme?: "light" | "dark", isOpen?: boolean, onClose?: () => void }> = observer(function ModalInfo(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), isOpen, onClose } = props;

  const createCycle = useCallback(() => {
    navigate('Home.Stack', 'Home.Stack', { screen: 'Home.Cycle.CreateMultiple' })
  }, [])

  return (<>
    <Modal isOpen={isOpen} onClose={onClose}>
    <View
      marginY="80px"
      marginX="16px"
      paddingY="24px"
      paddingX="16px"
      rounded="8px"
      width="80%"
      backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      alignItems="center"
      >
        <Text tx="farmAutoFeederPondListScreen.cycleClosedTitle" preset="title1" colorScheme={colorScheme} marginBottom="12px" textAlign="center" />
        <Text tx="farmAutoFeederPondListScreen.cycleClosedSubtitle" preset="body1" colorScheme={colorScheme} marginBottom="20px" textAlign="center" />
        <View flexDirection="row" alignItems="center" justifyContent="space-between">
          <Button
            testID="jala:id/cancel-button"
            tx="farmAutoFeederPondListScreen.cycleLaterButton"
            preset="outline"
            colorScheme={colorScheme}
            style={BUTTON_CLOSE_CYCLE}
            onPress={onClose}
            textPreset="title4"
          />
          <View width="10%" />
          <Button
            testID="jala:id/create-cycle-button"
            preset="primary"
            tx="farmAutoFeederPondListScreen.createCycleButton"
            style={BUTTON_CLOSE_CYCLE}
            colorScheme={colorScheme}
            onPress={createCycle}
            textPreset="title4"
          />
        </View>
      </View>
    </Modal>
  </>)
})

const ConnectingCard: FC <{
  colorScheme?: "light" | "dark",
  icon?: string,
  title?: string,
  onPressCard?: () => void,
  subtitle?: string, isFailed?: boolean, isConnected?: any, disabled?: boolean
}> = observer(function ConnectingCard(props) {
  const { colorScheme, icon, title, onPressCard, subtitle, isConnected, isFailed, disabled } = props

  const sub = subtitle || translate("autoFeederPondCreateWizardScreen.bluetoothDisconnected")
  const borderColor = isConnected ? color.primary : (isFailed ? color.palette.red400 : color.palette.gray500)

  return (
    <TouchableOpacity style={CONNECTING_CARD(borderColor, disabled)} onPress={onPressCard} disabled={disabled}>
      <MaterialIcons name={icon} size={24} color={disabled ? color.palette.gray500 : color.palette.gray800} />
      <View ml="16px">
        <Text text={title} preset="title4" color={disabled ? color.palette.gray500 : color.palette.gray800} colorScheme={colorScheme} />
        <Text text={sub} preset="body3" color={disabled ? color.palette.gray400 : color.palette.gray700} colorScheme={colorScheme} />
      </View>
    </TouchableOpacity>
  )
})

const ConnectingModal: FC<{ colorScheme?: "light" | "dark", onClose?: () => void, isOpen?: boolean }> = observer(function ConnectingModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), onClose, isOpen } = props;
  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()
  const [ isConnecting, setIsConnecting ] = useState<string>(null)
  const [ isNotConnected, setIsNotConnected ] = useState<boolean>(false)

  const {
    autoFeederPondStore: modelStore,
    // autoFeederPondStore: { loading }
  } = useStores()

  const connect = useCallback(async (value: "bluetooth"|"wifi" = "bluetooth") => {
    if (!model) return;
    setIsConnecting(value)
    modelStore.set('loading', true);
    onClose()
    await model.connect(value)
    .then(() => {
      setIsConnecting(null)
    })
    .catch(() => {
      setIsNotConnected(true)
      Toast.show({ title: translate('autoFeederPondDetailScreen.failedToConnect') })
      return null
    })
    if (model.isConnected) await model.commandSetDateTime()
    setModel(model)
    modelStore.set('loading', false);
  }, [model, modelStore, onClose, setModel])

  const ConnectingCardBT = useCallback(() => {
    const isBTConnected = model?.bluetoothDevice !== null
    const isNotConnectedTitle = isNotConnected ? translate("autoFeederPondDetailScreen.failedConnect") : translate("autoFeederPondCreateWizardScreen.bluetoothDisconnected")
    const isConnectedTitle = isBTConnected ? translate("autoFeederPondEditScreen.connected") : isNotConnectedTitle
    const connectingTitle = isConnecting === 'bluetooth' ? translate("autoFeederPondEditScreen.connecting") : isConnectedTitle
    return <ConnectingCard
      icon="bluetooth-audio"
      title={translate("autoFeederPondCreateWizardScreen.bluetooth")}
      subtitle={connectingTitle}
      onPressCard={() => connect("bluetooth")}
      isFailed={isNotConnected}
      isConnected={isBTConnected}
      disabled={false}
    />
  }, [connect, isConnecting, isNotConnected, model?.bluetoothDevice])

  const ConnectingCardWifi = useCallback(() => {
    const isWifionnected = model?.ip !== null
    const isBTConnected = model?.bluetoothDevice !== null
    const isNotConnectedTitle = isNotConnected ? translate("autoFeederPondDetailScreen.failedConnect") : translate("autoFeederPondCreateWizardScreen.bluetoothDisconnected")
    const isConnectedTitle = isWifionnected ? translate("autoFeederPondEditScreen.connected") : isNotConnectedTitle
    const connectingTitle = isConnecting === 'wifi' ? translate("autoFeederPondEditScreen.connecting") : isConnectedTitle
    return <ConnectingCard
      icon="wifi"
      title={translate("autoFeederPondEditScreen.wifi")}
      onPressCard={() => connect("wifi")}
      subtitle={connectingTitle}
      isFailed={isNotConnected}
      isConnected={isWifionnected}
      disabled={!isBTConnected}
    />
  }, [connect, isConnecting, isNotConnected, model?.bluetoothDevice, model?.ip])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <SafeAreaView />
      <View
        style={MODAL}
        borderTopRadius="20px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <View flexDirection="row" alignItems="center" justifyContent="space-between" px="16px">
          <Text tx="autoFeederPondCreateWizardScreen.connectAsto" preset="title3" color={color.palette.gray800} />
          <Button preset="link" textColor={color.primary} text={translate("common.close")} textPreset="title4" onPress={onClose} />
        </View>
        <View height="1px" backgroundColor={color.palette.gray200} mt="10px" mb="18px" />
        <ConnectingCardBT />
        <ConnectingCardWifi />
      </View>
    </Modal>
  )
})

const UploadButton : FC<{ colorScheme?: "light" | "dark", setProgress?: (number) => void, setUploading?: (state) => void, setUploadSucces?: () => void, uploading?: boolean }> = observer(function UploadButton(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    setProgress, setUploading, setUploadSucces, uploading
  } = props
  const { state: feedState } = useContext(FeedStateContext)
  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()
  const originalState = model && model.getAttributes().state;

  const {
    state: { feeds },
    dispatch: dispatchFeedState
  } = useContext(FeedStateContext)

  const {
    autoFeederPondStore: modelStore,
  } = useStores()

  const state = useMemo(() => (
    originalState ? model.getAttribute('state') : undefined
  ), [model, originalState])

  const timesPerDay = feedState.timesPerDay || state?.timesPerDay

  const img = require("./asto-upload.png")

  const upload = useCallback(async () => {
    // const uploadButton: AlertButton = {
      // text: translate('upload'), style: "default", onPress: async () => {
    let p = 0;
    const updateProgress = () => {
      setProgress(100 * p / (p + 2));
      p++;
    }
    updateProgress();

    setUploading(true);
    const response = await model.commandClear().catch()
    if (!response) {
      setUploading(false);
      Toast.show({ title: translate('failed') })
      return;
    }
    await delay(2000);

    updateProgress();
    for (let i = 0; i < feeds.length; i++) {
      if (!feeds[i].quantity) continue;
      if (state?.mode === 'normal') {
        for (let j = 0; j < feeds[i].feedsPerDay.length; j++) {
          if (!feeds[i].feedsPerDay[j]?.time) continue;
          const response = await model.commandSetFeedPerDay(feeds[i], feeds[i].feedsPerDay[j]);
          if (!response) {
            setUploading(false);
            Toast.show({ title: translate('failed') })
            return;
          }
          await delay(1000);
          updateProgress();
        }
      } else {
        const response = await model.commandSetFeed(feeds[i])
        if (!response) {
          setUploading(false);
          Toast.show({ title: translate('failed') })
          return;
        }
        await delay(1000);
        updateProgress();
      }
    }
    await model.commandSaveChanges(5000).catch()
    await delay(2000);
    setProgress(100);
    setUploading(false);
    await modelStore.updateOffline(model, {
      state: {
        ...state,
        timesPerDay,
        updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        get uploadedAt() { return this.updatedAt },
        feeds: feeds.filter((f) => f.quantity)
          .map(({ feed_at, quantity, onTimeOverride = null, intervalOverride = null, feedsPerDay = [] }) => ({
            feed_at, quantity, onTimeOverride, intervalOverride, feedsPerDay
          }))
      }
    })
    setModel(model)
    dispatchFeedState({ type: "reset", payload: { originalTimesPerDay: timesPerDay, originalFeeds: feeds } })
    JalaAnalytic.log('Mobile App: Asto - Upload Data', { feeds });
    modelStore.set('isFeedChanged', false)
    setUploadSucces()
    // Toast.show({ title: translate('success') });
  }, [dispatchFeedState, feeds, model, modelStore, setModel, setProgress, setUploadSucces, setUploading, state, timesPerDay])

  return (
    <View position="absolute" right={0} bottom={0} left={0}>
      <SafeAreaView />
      <View
        marginX="16px"
        marginBottom="16px"
        paddingLeft="6px"
        paddingRight="6px"
        paddingY="4px"
        borderRadius="8px"
        shadow={4}
        backgroundColor={color.palette.white}
      >
        <View flexDirection="row" alignItems="center" justifyContent="space-between">
          <View flexDirection="row" alignItems="center">
            <AutoImage source={img} style={ASTO_UPLOAD_IMAGE} />
            <Text preset="title4" tx="autoFeederPondDetailScreen.uploadBannerTitle" colorScheme={colorScheme} />
          </View>
          <Button
            testID="jala:id/upload-button"
            preset="primary"
            onPress={upload}
            colorScheme={colorScheme}
          >
            { uploading
              ? <ActivityIndicator size={18} color={color.palette.white} />
              : <Text tx="autoFeederPondDetailScreen.uploadBannerButton" preset="title4" color={color.palette.white} />
            }
          </Button>
        </View>
      </View>
    </View>
  )
})

export const CalibrationInfo: FC<IViewProps & { colorScheme?: "light" | "dark", model: AutoFeederPond, onClose?: () => void }> = observer(function CalibrationInfo(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    model,
    onClose: onCloseProp,
    ...restProps
  } = props;
  const { isOpen, onClose, onOpen } = useDisclose()
  const onCloseModal = useCallback((success: boolean) => {
    onClose()
    if (success) onCloseProp && onCloseProp()
  }, [onClose, onCloseProp])

  return (
    <View padding="12px" backgroundColor={color.palette.orange300} {...restProps}>
      <View flexDirection="row" justifyContent="space-between" mb="8px">
        <Text tx="autoFeederPondDetailScreen.calibrationTitle" preset="title4" color={color.palette.gray800} />
        <TouchableOpacity style={CLOSE_ICON} onPress={onCloseProp} >
          <JalaIcon name="close" size={20} color={color.palette.gray800} />
        </TouchableOpacity>
      </View>
      <AutoFeederCalibrationModal model={model} isOpen={isOpen} onClose={onCloseModal}>
        <Button
          tx="autoFeederPondDetailScreen.calibrationButton"
          preset="primary"
          style={CALIBRATION_BUTTON}
          onPress={onOpen}
          colorScheme={colorScheme}
        />
      </AutoFeederCalibrationModal>
    </View>
  )
})

const FeedStateContext = createContext<{ state: FeedState, dispatch: (action: FeedAction) => any }>({
  state: {
    modified: false,
    originalTimesPerDay: 1,
    timesPerDay: 1,
    originalFeeds: [],
    feeds: [],
    originalFeedsPerDay: [],
  },
  dispatch() { return this.state }
})

const timeToSeconds = (time) => {
  if (!time) return null;
  const parts = time.split(":");
  return (+parts[0] * 3600) + (+parts[1] * 60) + (+parts[2]);
}

const totalTimeCalculation = (start, end) => {
  const startSeconds = timeToSeconds(start);
  const endSeconds = timeToSeconds(end);
  const calculateMinutes = moment(end, 'HH:mm:ss').diff(moment(start, 'HH:mm:ss'), 'minutes', true)
  if(startSeconds > endSeconds) return calculateMinutes + 1440
  return calculateMinutes
}

const transformFeed = (feed: any) => {
  const onGram = feed.debit * feed.onTime
  const periodTimes = feed.quantity * 1000 / onGram
  const totalTime = totalTimeCalculation(feed.startTime, feed.endTime)
  const interval = feed.quantity ? totalTime / periodTimes : null
  return ({
    ...feed,
    interval
  })
}

const transformFeedPerDay = (feedPerDay: any) => {
  return ({
    ...feedPerDay
  })
}

const feedStateReducer = (state: FeedState, action: FeedAction) => {
  if (action.type === 'reset') {
    return {
      modified: false,
      originalTimesPerDay: action.payload?.originalTimesPerDay || state.originalTimesPerDay,
      timesPerDay: action.payload?.originalTimesPerDay || state.originalTimesPerDay,
      originalFeeds: [...action.payload?.originalFeeds || state.originalFeeds],
      feeds: [...action.payload?.originalFeeds || state.originalFeeds],
      originalFeedsPerDay: [...action.payload?.originalFeedsPerDay || state.originalFeedsPerDay],
    } as FeedState
  } else if (action.type === 'update-feed') {
    const itemIndex = action.index || state.feeds.findIndex((s) => s.feed_at === action.payload?.feed_at)
    if (itemIndex < 0) return state
    state.feeds[itemIndex] = transformFeed({
      ...state.feeds[itemIndex],
      ...action.payload
    })
    state.modified = true
    return {...state} as FeedState
  } else if (action.type === 'update-feed-per-day') {
    const { feed_at, ...payload } = action.payload
    const feedIndex = state.feeds.findIndex((s) => s.feed_at === feed_at)
    const itemIndex = action.index
    if (feedIndex < 0 || itemIndex < 0) return state
    state.feeds[feedIndex].feedsPerDay[itemIndex] = {
      ...state.feeds[feedIndex].feedsPerDay[itemIndex],
      ...payload
    }
    const lastPercent = 100 - _.sumBy(state.feeds[feedIndex].feedsPerDay, 'percent')
    if (state.feeds[feedIndex].feedsPerDay.length)
      state.feeds[feedIndex].feedsPerDay[state.feeds[feedIndex].feedsPerDay.length - 1].percent += lastPercent;
    state.modified = true
    state.feeds[feedIndex].feedsPerDay = _.sortBy(state.feeds[feedIndex].feedsPerDay, 'time')
    return {
      ...state
    } as FeedState
  } else if (action.type === 'set-modified') {
    return { ...state, modified: action.payload }
  } else if (action.type === 'set-times-per-day') {
    for (let i = 0; i < state.feeds.length; i++) {
      const feedsPerDay = [];
      const originalFeedPerDay = state.originalFeedsPerDay[0];
      const timesPerDay = (typeof action.payload === 'number') && action.payload || 1
      const intervalPeriod = originalFeedPerDay?.intervalPeriod || 60;
      const onTime = originalFeedPerDay?.onTime || 1;
      const debit = originalFeedPerDay?.debit || 350;
      for (let j = 0; j < timesPerDay; j++) {
        const feedPerDay = ((state.originalFeedsPerDay || [])[j] || {});
        feedsPerDay[j] = transformFeedPerDay({
          time: null,
          percent: 100 / timesPerDay,
          ...feedPerDay,
          intervalPeriod,
          onTime,
          debit,
        })
      }
      if (state.feeds[i])
        state.feeds[i] = {
          ...state.feeds[i],
          feedsPerDay
        }
    }
    return {
      ...state,
      timesPerDay: (typeof action.payload === 'number') && action.payload || 1
    }
  } else {
    return state as FeedState
  }
}

export const AutoFeederPondDetailActivity: FC<{ colorScheme?: "light" | "dark" }> = observer(function AutoFeederPondDetailActivity(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  // Pull in one of our MST stores
  const {
    appStore: { farmStore: { pondStore: { autoFeederPond } } }
  } = useStores()

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  return (
    <View style={TOP_BAR_CONTAINER}>
      <TouchableOpacity style={ACTIVITY_BUTTON} onPress={() => navigate('Home.Stack', 'Home.AutoFeederPond.Activity.List', autoFeederPond)}>
        <Icon {...iconBaseStyle} as={MaterialIcons} name="history" size="24px" color={color.palette.white} />
      </TouchableOpacity>
    </View>
  )
})

export const AutoFeederPondDetailSetting: FC<{ colorScheme?: "light" | "dark" }> = observer(function AutoFeederPondDetailSetting(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  // Pull in one of our MST stores
  const {
    appStore: { farmStore: { pondStore: { autoFeederPond } } }
  } = useStores()

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  return (
    <View style={TOP_BAR_CONTAINER}>
      <TouchableOpacity style={RIGHT_BUTTON} onPress={() => navigate('Home.Stack', 'Home.AutoFeederPond.Edit', autoFeederPond)}>
        <Icon {...iconBaseStyle} as={JalaIcon} name="setting" size="24px" color={color.palette.white} />
      </TouchableOpacity>
    </View>
  )
})

const ConnectionSetup: FC<{ colorScheme?: "light" | "dark", isWifiConnected?: boolean, isBTConnected?: boolean, onShowConnectModal?: () => void }> = observer(function ConnectionSetup(props) {
  const { colorScheme, isWifiConnected, isBTConnected, onShowConnectModal } = props;

  const status = isBTConnected || isWifiConnected ? translate("autoFeederPondEditScreen.connected") : translate("autoFeederPondEditScreen.disconnected");
  const statusColor = isBTConnected || isWifiConnected ? color.palette.green500 : color.palette.gray500;
  return (
    <TouchableOpacity onPress={onShowConnectModal} disabled={isWifiConnected && isBTConnected}>
      <View flexDirection="row" alignItems="center" px="10px" py="4px" backgroundColor={color.palette.gray100} borderRadius={20}>
        <Text text={status} preset="label2" colorScheme={colorScheme} color={color.palette.gray800} mr="4px" />
        {!isWifiConnected && <View>
          {!isBTConnected && <View position="absolute" zIndex="1" shadow={2} alignSelf="flex-end" marginTop="-4px" marginLeft="10px">
            <MaterialCommunityIcons name="alert-circle" size={11} color={color.palette.red500} />
          </View>}
          <View alignItems="center" justifyContent="center" width="20px" height="20px" backgroundColor={statusColor} borderRadius={20}>
            <MaterialIcons name="bluetooth-audio" size={12} color={color.palette.white} />
          </View>
        </View>}
        {!(isBTConnected && !isWifiConnected) && <View>
          {!isWifiConnected && <View position="absolute" zIndex="1" shadow={2} alignSelf="flex-end" marginTop="-4px" marginLeft="10px">
            <MaterialCommunityIcons name="alert-circle" size={11} color={color.palette.red500} />
          </View>}
          <View alignItems="center" justifyContent="center" width="20px" height="20px" backgroundColor={statusColor} borderRadius={20} ml="4px">
            <MaterialIcons name="wifi" size={12} color={color.palette.white} />
          </View>
        </View>}
      </View>
    </TouchableOpacity>
  )
})

const ConfirmationModal: FC<{
  colorScheme?: "light" | "dark", isOpen?: boolean, onClose?: () => void, title?: string, subtitle?: string, onConfirm?: () => void
}> = observer(function ConfirmationModal(props) {
  const { colorScheme, isOpen, onClose, onConfirm, title, subtitle } = props;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="28px"
        paddingY="24px"
        paddingX="16px"
        rounded="16px"
        backgroundColor={nativeBaseTheme.colors.light["50"]}
      >
        <Text
          text={title}
          preset="title1"
          colorScheme={colorScheme}
          marginBottom="12px"
          textAlign="center"
          color={color.palette.gray800}
        />
        <Text
          text={subtitle}
          preset="body1"
          colorScheme={colorScheme}
          marginBottom="20px"
          textAlign="center"
          color={color.palette.gray800}
        />
        <View flexDirection="row" alignItems="center" padding="16px" justifyContent="space-between">
          <Button
            testID="jala:id/left-button"
            text={translate("No").toUpperCase()}
            colorScheme={colorScheme}
            onPress={onClose}
            style={MODAL_BUTTON}
            preset="outline"
            textColor={color.palette.blue700}
          />
          <Button
            testID="jala:id/right-button"
            text={translate("Yes").toUpperCase()}
            colorScheme={colorScheme}
            onPress={onConfirm}
            style={MODAL_BUTTON_RIGHT}
            textColor={color.palette.white}
          />
        </View>
      </View>
    </Modal>
  )
})

const TitleSection: FC<{
  colorScheme?: "light"|"dark", scrollViewRef?: { current: ScrollView }, onShowConnectModal?: () => void
}> = memo(observer(function TitleSection(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    scrollViewRef,
    onShowConnectModal
  } = props
  const { autoFeederPond: model } = useAutoFeederPond()

  return <View paddingX="8px" paddingY="16px" flexDirection="row" justifyContent="space-between" alignItems="center">
    <View flex={1}>
      <Text preset="display3" text={(model.name || model.auto_feeder_address)} colorScheme={colorScheme} isTruncated />
      <Text preset="body3" text={!!model.name && model.auto_feeder_address || ' '} colorScheme={colorScheme} color={color.primary} />
    </View>
    <View flex={1} flexDirection="row" justifyContent="flex-end">
      <WalkthroughElement
        id="asto-connected-state"
        scrollViewRef={scrollViewRef}
        scrollOffset={250}
      >
        <ConnectionSetup isBTConnected={model?.bluetoothDevice !== null} isWifiConnected={model?.ip !== null} onShowConnectModal={onShowConnectModal} />
      </WalkthroughElement>
    </View>
  </View>
}))

const DetailSection: FC<{ colorScheme?: "light"|"dark", scrollViewRef?: { current: ScrollView }, uploading?: boolean, progress?: number }> = memo(observer(function DetailSection(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { state: feedState } = useContext(FeedStateContext)
  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()
  const originalState = model && model.getAttributes().state;
  // const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });
  const { isOpen, onClose } = useDisclose();

  const {
    state: { feeds, originalFeedsPerDay },
    dispatch: dispatchFeedState
  } = useContext(FeedStateContext)

  const {
    autoFeederPondStore: modelStore,
  } = useStores()

  const state = useMemo(() => (
    originalState ? model.getAttribute('state') : undefined
  ), [model, originalState])

  const timesPerDay = feedState.timesPerDay || state?.timesPerDay

  const saveData = useCallback(async () => {
    onClose()
    await modelStore.updateOffline(model, {
      state: {
        ...state,
        timesPerDay,
        updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        feeds: feeds.filter((f) => f.quantity)
          .map(({ feed_at, quantity, onTimeOverride = null, intervalOverride = null, feedsPerDay = [] }) => ({
            feed_at, quantity, onTimeOverride, intervalOverride, feedsPerDay
          })),
      }
    })
    setModel(model)
    dispatchFeedState({ type: "reset", payload: { originalTimesPerDay: timesPerDay, originalFeeds: feeds, originalFeedsPerDay: feeds[0]?.feedsPerDay || originalFeedsPerDay } })
    JalaAnalytic.log('Mobile App: Asto - Save Data', { feeds });
    Toast.show({ title: translate('success') });
  }, [dispatchFeedState, feeds, model, modelStore, onClose, originalFeedsPerDay, setModel, state, timesPerDay])

  const Confirmation = useCallback(() => {
    const title = "autoFeederPondDetailScreen.confirmationUploadTitle";
    const subtitle = "autoFeederPondDetailScreen.confirmationUploadSubtitle"
    // const onConfirm = confirmationState === "save" ? saveData : upload
    return (
    <ConfirmationModal isOpen={isOpen} title={translate(title)} subtitle={translate(subtitle)} onClose={onClose} onConfirm={saveData} />
  )}, [isOpen, onClose, saveData])

  return <View padding="8px" flexDirection="row" alignItems="center">
    <View width="180px" alignItems="center">
      <AutoImage source={model.isConnected ? autoFeederImage : autoFeederDisabledImage} style={IMAGE_AUTO_FEEDER} />
    </View>
    <View flex={1}>
      <Confirmation />
      {state?.mode === 'normal' ? (<View flex={1}>
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.mode" preset="body2" colorScheme={colorScheme} />
          <Text text={state?.mode ? translate(`autoFeederPondDetailScreen.${state.mode}` as TxKeyPath) : "-"} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        {/* <View flexDirection="row" justifyContent="space-between"> */}
        {/*   <Text tx="autoFeederPondDetailScreen.debit" preset="body2" colorScheme={colorScheme} /> */}
        {/*   <Text text={`${state?.debit ? numeral(state.debit).format("0,0.[0]") : "-"} ${translate("autoFeederPondDetailScreen.debitUnit")}`} preset="body2" colorScheme={colorScheme} /> */}
        {/* </View> */}
        {/* <Divider my="4px" /> */}
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.throwDebit" preset="body2" colorScheme={colorScheme} />
          <Text text={`${model?.throwDebit ? numeral(model.throwDebit).format("0,0.[0]") : "-"} ${translate("autoFeederPondDetailScreen.throwDebitUnit")}`} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.timesPerDay" preset="body2" colorScheme={colorScheme} />
          <Text text={`${numeral(timesPerDay).format("0,0")} ${translate("autoFeederPondDetailScreen.timesPerDayUnit")}`} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.onTime" preset="body2" colorScheme={colorScheme} />
          <Text text={`${numeral(state?.onTime || 1).format('0,0.[00]')} ${translate('autoFeederPondDetailScreen.onTimeUnit')}`} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.intervalPeriod" preset="body2" colorScheme={colorScheme} />
          <Text text={`${numeral(state?.intervalPeriod || 60).format('0,0.[00]')} ${translate('autoFeederPondDetailScreen.intervalUnitShort')}`} preset="body2" colorScheme={colorScheme} />
        </View>
      </View>) : (<View flex={1}>
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.mode" preset="body2" colorScheme={colorScheme} />
          <Text text={state?.mode ? translate(`autoFeederPondDetailScreen.${state.mode}` as TxKeyPath) : '-'} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        {/* <View flexDirection="row" justifyContent="space-between"> */}
        {/*   <Text tx="autoFeederPondDetailScreen.debit" preset="body2" colorScheme={colorScheme} /> */}
        {/*   <Text text={`${state?.debit ? numeral(state.debit).format('0,0.[0]') : '-'} ${translate('autoFeederPondDetailScreen.debitUnit')}`} preset="body2" colorScheme={colorScheme} /> */}
        {/* </View> */}
        {/* <Divider my="4px" /> */}
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.throwDebit" preset="body2" colorScheme={colorScheme} />
          <Text text={`${model?.throwDebit ? numeral(model.throwDebit).format("0,0.[0]") : "-"} ${translate("autoFeederPondDetailScreen.throwDebitUnit")}`} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.startTime" preset="body2" colorScheme={colorScheme} />
          <Text text={state?.startTime || '-'} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.endTime" preset="body2" colorScheme={colorScheme} />
          <Text text={state?.endTime || '-'} preset="body2" colorScheme={colorScheme} />
        </View>
        <Divider my="4px" />
        <View flexDirection="row" justifyContent="space-between">
          <Text tx="autoFeederPondDetailScreen.onTime" preset="body2" colorScheme={colorScheme} />
          <Text text={`${numeral(state?.onTime || '-').format('0,0.[00]')} ${translate('autoFeederPondDetailScreen.onTimeUnit')}`} preset="body2" colorScheme={colorScheme} />
        </View>
      </View>)}
      <Divider my="4px" />
      <TouchableOpacity onPress={() => navigate('Home.Stack', 'Home.AutoFeederPond.Edit', model)}>
        <Text tx="overviewScreen.setting" preset="title4" color={color.primary} />
      </TouchableOpacity>
    </View>
  </View>;
}))

const TodaySection: FC<{ colorScheme?: "light"|"dark", scrollViewRef?: any, setProgress?: (number) => void, setUploading?: (state) => void, setUploadSucces?: () => void, uploading?: boolean }> = memo(observer(function TodaySection(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), scrollViewRef } = props
  const {
    appStore: { farmStore: { pondStore, pondStore: { loadingAutoFeederPond, activeCycle } } },
    autoFeederPondStore: modelStore,
    autoFeederPondStore: { isFeedChanged }
  } = useStores()
  const { isOpen, onClose, onOpen } = useDisclose()
  const [ isContinuosHidden, setContinuousHidden ] = useState<boolean>(true)
  // const { todayCycleStoreModel } = pondStore || PondStoreModel.create({})

  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()
  const originalState = model && model.getAttributes().state;
  const state = useMemo(() => (
    originalState ? model.getAttribute('state') : undefined
  ), [model, originalState])

  const {
    // state: feedState,
    state: { modified, feeds },
    dispatch: dispatchFeedState
  } = useContext(FeedStateContext)

  const todayFeed = (feeds || [])[0]
  // const timesPerDay = feedState.timesPerDay || state?.timesPerDay


  const override = useCallback(async (data) => {
    // const onTimeData = data.onTimeOverride === 0 ? todayFeed.onTime : data.onTimeOverride || todayFeed.onTimeOverride
    // const intervalData = data.intervalOverride === 0 ? todayFeed.interval : data.intervalOverride || todayFeed.intervalOverride
    const overrideButton: AlertButton = {
      text: translate('override'), style: "default", onPress: async () => {
        // await model.commandOnTimeAndIntervalOverride(onTimeData, intervalData).catch()
        dispatchFeedState({ type: "update-feed", payload: { ...todayFeed, ...data } })
        dispatchFeedState({ type: "set-modified", payload: modified })
        // Toast.show({ title: 'Uploaded' })
        await modelStore.updateOffline(model, {
          state: {
            ...state,
            updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
            // get uploadedAt() { return this.updatedAt },
            feeds: feeds.filter((f) => f.quantity)
              .map(({ feed_at, quantity, onTimeOverride = null, intervalOverride = null }) => ({
                feed_at, quantity, onTimeOverride, intervalOverride
              }))
          }
        })
        setModel(model)
      }
    }
    Alert.alert(translate('areYouSureYouWantTo', { something: _.lowerCase(translate('override')) }), undefined, [
      { text: translate('cancel'), style: "cancel" },
      Platform.OS !== 'web' ? undefined : overrideButton,
      Platform.OS === 'web' ? undefined : overrideButton,
    ])
  }, [dispatchFeedState, feeds, model, modelStore, modified, setModel, state, todayFeed])

  const onPressEdit = useCallback(() => {
    if(activeCycle) {
      pondStore.set('cycleId', activeCycle.id)
      navigate("Home.Stack", "Home.AutoFeederPond.DailyFeed.CreateMultiple", {
        feed_at: todayFeed?.feed_at,
        auto_feeder_pond_id: model?.id,
      })
    } else {
      onOpen()
    }
  }, [activeCycle, model?.id, onOpen, pondStore, todayFeed?.feed_at])

  const FeedSection = useCallback(() => (
    <View flexDirection="row" padding="12px">
      <View flex={1}>
        <View flexDirection="row" alignItems="center">
          <Text
            tx="autoFeederPondDetailScreen.todayFeedTitle"
            preset="title4"
            colorScheme={colorScheme}
          />
          <Text
            flex={1}
            text={`${todayFeed.quantity && numeral(todayFeed.quantity).format('0,0.[00]') || '-'} kg`}
            preset="title3"
            color={color.primary}
            marginLeft="9px"
          />
        </View>
        <Text text={moment(todayFeed.feed_at).format('DD MMMM YYYY')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View>
        <Button
          text={todayFeed.quantity ? translate("edit") : translate("add")}
          preset={todayFeed.quantity ? "outline" : "primary"}
          colorScheme={colorScheme}
          textPreset="title4"
          style={BUTTON}
          onPress={onPressEdit}
          disabled={loadingAutoFeederPond}
        />
      </View>
    </View>
  ), [colorScheme, loadingAutoFeederPond, onPressEdit, todayFeed.feed_at, todayFeed.quantity])

  const onSetContinuosHidden = useCallback(() => setContinuousHidden(!isContinuosHidden),[isContinuosHidden])

  return <>
    <WalkthroughElement
      id="asto-today-feed"
      scrollViewRef={scrollViewRef}
      scrollOffset={250}
    >
      <View borderWidth={1} borderRadius={10} borderColor={color.palette.gray200}  mt="16px" mx="16px" mb="8px">
        <FeedSection/>
        {state?.mode === 'normal' ?
          <WalkthroughElement
            id="asto-today-feed-normal"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <View flexDirection="row" borderWidth={0.5} borderColor={color.palette.gray200} backgroundColor={color.palette.gray100}>
              <View flex={1} padding="5px" alignItems="center">
                <Text
                  tx="autoFeederPondDetailScreen.time"
                  preset="title5"
                  colorScheme={colorScheme}
                />
              </View>
              <View flex={1} padding="5px" alignItems="center">
                <Text tx="autoFeederPondDetailScreen.quantity" preset="title5" colorScheme={colorScheme} />
              </View>
              <View flex={1} padding="5px" alignItems="center">
                <Text tx="autoFeederPondDetailScreen.frequency" preset="title5" colorScheme={colorScheme} />
              </View>
            </View>
            <View marginBottom="12px">
              {(todayFeed.feedsPerDay || []).map((feedPerDay, index) => (
                <View key={`${index}`} flexDirection="row" backgroundColor={index % 2 === 0 ? color.palette.white : '#F9F9F9'} borderBottomRadius={index === todayFeed.feedsPerDay?.length - 1 ? 10 : 0}>
                  <View flex={1} padding="5px" flexDirection="row" justifyContent="center">
                    <View flex={1} alignItems="center">
                      <Text text={feedPerDay.time && `${moment(feedPerDay.time, 'HH:mm:ss').format('HH:mm')} - ${moment(feedPerDay.time, 'HH:mm:ss').add(feedPerDay.intervalPeriod, 'minutes').format('HH:mm')}` || '-'} preset="body2" colorScheme={colorScheme} />
                    </View>
                  </View>
                  <View flex={1} padding="5px" flexDirection="row" justifyContent="center">
                    <Text text={`${numeral(todayFeed.quantity * feedPerDay.percent / 100).format('0,0.[00]') || '-'} kg`} preset="body2" colorScheme={colorScheme} />
                  </View>
                  <View flex={1} padding="5px" flexDirection="row" justifyContent="center">
                    <Text text={`${(numeral(0.499 + (todayFeed.quantity * 1000 * feedPerDay.percent / 100 / (state?.onTime * state?.debit))).format('0,0'))} x ${numeral((state?.onTime || 1) * (state?.debit || 350)).format('0,0.[00]')} gr`} preset="body2" colorScheme={colorScheme} />
                  </View>
                </View>
              ))}
            </View>
          </WalkthroughElement> :
          <View borderTopWidth={1} borderBottomWidth={1} borderColor={color.palette.gray300} padding="12px" >
            <View flexDirection="row" justifyContent="space-between">
              <Text tx="autoFeederPondDetailScreen.intervalShort" preset="title4" color={color.palette.gray800} />
              <Text text={`${todayFeed.interval ? numeral(todayFeed.interval).format("0,0.[00]") : "-"} ${translate('autoFeederPondCreateWizardScreen.intervalPeriodUnit')}`} preset="body2" color={color.palette.gray800} alignSelf="flex-end" />
            </View>
          </View>
        }
        <View borderTopWidth={1} borderTopColor={color.palette.gray200}>
          <TouchableOpacity style={ADVANCED_SETTING} onPress={onSetContinuosHidden}>
            <Text tx="autoFeederPondDetailScreen.advancedSetting" preset="title5" color={color.palette.gray900} />
            <JalaIcon name={isContinuosHidden ? "expand_more" : "expand_less"} color={color.palette.gray900} size={24} />
          </TouchableOpacity>
          {!isContinuosHidden && <View px="12px" pb="12px">
            <View flexDirection="row" alignItems="center" justifyContent="flex-start">
              <Text tx="autoFeederPondDetailScreen.intervalShort" preset="title4" colorScheme={colorScheme} mr="6px" />
              {/* <InfoModal title={translate("autoFeederPondDetailScreen.intervalInfo")}>
                <JalaIcon name="info-1" size={16} color={color.palette.gray800} />
              </InfoModal> */}
            </View>
            <SettingItem
              isDisabled={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond}
              type="numeral-input"
              colorScheme={colorScheme}
              actionsheetProps={{
                title: translate('autoFeederPondDetailScreen.intervalDataTitle')
              }}
              value={todayFeed.intervalOverride && todayFeed.intervalOverride > 0 ? numeral(todayFeed.intervalOverride).format("0,0.[00]") : null}
              onValueChange={(value) => {
                if (!modified && model.isConnected) override({ intervalOverride: Math.abs(value) }).catch()
                else dispatchFeedState({ type: "update-feed", index: 0, payload: { ...todayFeed, intervalOverride: Math.abs(value) } })
              }}
              formControlProps={{
                labelTx: "autoFeederPondDetailScreen.intervalOverride",
                inputProps: {
                  InputRightElement: (<Text text={translate(`autoFeederPondCreateWizardScreen.intervalPeriodUnit` as TxKeyPath)} mr="8px" preset="title4" />),
                  placeholder: "-"
                }
              }}
            >
              <View flexDirection="row" borderWidth={1} borderColor={color.palette.gray200} backgroundColor={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond ? color.palette.gray100 : color.palette.white} borderRadius={5} justifyContent="space-between" alignItems="center" padding="8px" mt="10px">
                <Text
                  text={todayFeed.intervalOverride ? numeral(todayFeed.intervalOverride).format("0,0.[00]") : "-"}
                  preset="body2"
                  color={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond ? color.palette.gray500 : color.palette.gray800}
                />
                <Text text={translate("autoFeederPondCreateWizardScreen.intervalPeriodUnit")} preset="title5" color={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond ? color.palette.gray500 : color.palette.gray800} />
              </View>
            </SettingItem>
            {/* {!todayFeed.intervalOverride && (<View flexDirection="row" alignItems="center">
              <Text tx="autoFeederPondCreateWizardScreen.intervalHelper" preset="caption2" mt="4px" color={color.palette.gray800} />
              <Text text={`${todayFeed.interval && todayFeed.quantity ? numeral(todayFeed.interval).format("0,0.[00]") : "-"} ${translate('autoFeederPondCreateWizardScreen.intervalPeriodUnit')}`} preset="title5" mt="4px" color={color.palette.gray800} />
            </View>)} */}
            <View flexDirection="row" alignItems="center" mt="18px">
              <Text tx="autoFeederPondCreateWizardScreen.onTime" preset="title4" colorScheme={colorScheme} mr="6px" />
              {/* <InfoModal title={translate("autoFeederPondDetailScreen.durationInfo")}>
                <JalaIcon name="info-1" size={16} color={color.palette.gray800} />
              </InfoModal> */}
            </View>
            <SettingItem
              isDisabled={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond}
              type="numeral-input"
              colorScheme={colorScheme}
              actionsheetProps={{
                title: translate('autoFeederPondCreateWizardScreen.onTime')
              }}
              value={todayFeed.onTimeOverride ? numeral(todayFeed.onTimeOverride).format("0,0.[00]") : null}
              onValueChange={(value) => {
                if (!modified && model.isConnected) override({ onTimeOverride: Math.abs(value) }).catch()
                else dispatchFeedState({ type: "update-feed", index: 0, payload: { ...todayFeed, onTimeOverride: Math.abs(value) } })
              }}
              formControlProps={{
                labelTx: "autoFeederPondDetailScreen.onTimeOverride",
                inputProps: {
                  InputRightElement: (<Text text={translate(`autoFeederPondCreateWizardScreen.onTimeUnit` as TxKeyPath)} mr="8px" preset="title4" />),
                  placeholder: "-"
                }
              }}
            >
              <View flexDirection="row" borderWidth={1} borderColor={color.palette.gray200} backgroundColor={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond ? color.palette.gray100 : color.palette.white} borderRadius={5} justifyContent="space-between" alignItems="center" padding="8px" mt="10px">
                <Text
                  text={todayFeed.onTimeOverride ? numeral(todayFeed.onTimeOverride).format("0,0.[00]") : "-"}
                  preset="body2"
                  color={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond ? color.palette.gray500 : color.palette.gray800}
                />
                <Text text={translate("autoFeederPondCreateWizardScreen.onTimeUnit")} preset="title5" color={isFeedChanged || !todayFeed.quantity || loadingAutoFeederPond ? color.palette.gray500 : color.palette.gray800} />
              </View>
            </SettingItem>
            {/* {!todayFeed.onTimeOverride && <View flexDirection="row" alignItems="center">
              <Text tx="autoFeederPondCreateWizardScreen.onHelper" preset="caption2" mt="4px" color={color.palette.gray800} />
              <Text text={`${todayFeed.onTime ? numeral(todayFeed.onTime).format("0,0.[00]") : "-"} ${translate('autoFeederPondCreateWizardScreen.onTimeUnit')}`} preset="title5" mt="4px" color={color.palette.gray800} />
            </View>} */}
          </View>}
        </View>
      </View>
      <ModalCycleClosed isOpen={isOpen} onClose={onClose} />
    </WalkthroughElement>
  </>
}));

const NextDaySection: FC<{ colorScheme?: "light"|"dark", scrollViewRef?: any }> = memo(observer(function NextDaySection(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { autoFeederPond: model } = useAutoFeederPond()
  const originalState = model && model.getAttributes().state;
  const state = useMemo(() => (
    originalState ? model.getAttribute('state') : undefined
  ), [model, originalState])
  const { state: { feeds } } = useContext(FeedStateContext)
  const {
    appStore: { farmStore: { pondStore: { loadingAutoFeederPond } } },
    autoFeederPondStore: { isFeedChanged }
  } = useStores()
  const nextFeeds = (feeds || []).slice(1)

  const onPressEdit = useCallback((feed_at) => {
    navigate("Home.Stack", "Home.AutoFeederPond.DailyFeed.CreateMultiple", {
      feed_at: feed_at,
      auto_feeder_pond_id: model?.id,
    })
  }, [model?.id])

  return (
      <View borderWidth={1} borderColor={color.palette.gray200} borderRadius={10} m="16px" mb={model.isConnected ? "70px" : "16px"}>
        <View
          px="12px"
          py="14px"
          justifyContent="center"
          // borderTopWidth="4px"
          borderColor={`gray.${colorScheme === 'light' ? 200 : 800}`}
        >
          <Text tx="autoFeederPondDetailScreen.weeklySchedule" colorScheme={colorScheme} preset="title4" color={color.palette.gray800} />
        </View>
          {state?.mode === 'normal' ? <View>
            <View flexDirection="row" alignItems="center" py="8px" px="12px" borderTopWidth={1} borderColor={color.palette.gray200} backgroundColor={color.palette.gray100}>
              <View flex={1}>
                <Text tx="autoFeederPondDetailScreen.date" preset="title4" colorScheme={colorScheme} />
              </View>
              <View flex={1} alignItems="center">
                <Text tx="total" preset="title4" colorScheme={colorScheme} />
              </View>
              <View flex={1} alignItems="center">
                <InfoModal title={translate("autoFeederPondDetailScreen.detailInfo")}>
                  <View flexDirection="row" alignItems="center">
                    <Text tx="autoFeederPondDetailScreen.detail" preset="title4" colorScheme={colorScheme} />
                    <JalaIcon name="info-1" size={16} color={color.palette.gray800} />
                  </View>
                </InfoModal>
              </View>
              <View flex={1} alignItems="flex-end">
                <Text tx="edit" preset="title4" colorScheme={colorScheme} />
              </View>
            </View>
            <Divider />
            <View>
              {nextFeeds.map((nextFeed, index) => {
                const feedPerDay = (nextFeed.feedsPerDay || [])
                  .map((f) => numeral(f.percent * nextFeed.quantity / 100).format('0,0.[0]'))

                return(
                  <View key={`${index}`} flexDirection="row" px="12px" py="8px" alignItems="flex-start" backgroundColor={index %2 === 0 ? color.palette.white : color.palette.gray100 }>
                    <View flex={1} alignItems="flex-start">
                      <Text text={moment(nextFeed.feed_at).format("DD MMM")} preset="body2" colorScheme={colorScheme} />
                    </View>
                    <View flex={1} alignItems="center">
                      {loadingAutoFeederPond
                        ? <ActivityIndicator size={18} color={color.primary} style={INDICATOR} />
                        : <Text text={`${nextFeed.quantity && numeral(nextFeed.quantity).format("0,0.[00]") || "-"} kg`}
                            preset="body2" colorScheme={colorScheme} mr="5px" />
                      }
                    </View>
                    <View flex={1} alignItems="center">
                      <Text text={`${nextFeed.quantity && feedPerDay.join(', ') || "-"}`}
                            preset="body2" colorScheme={colorScheme} />
                    </View>
                    <View flex={1} alignItems="flex-end">
                      <SettingItem
                        type="action"
                        colorScheme={colorScheme}
                        onPress={() => onPressEdit(nextFeed.feed_at)}
                        isDisabled={loadingAutoFeederPond}
                      >
                        <MaterialCommunityIcons name="pencil" color={color.primary} size={16} />
                      </SettingItem>
                    </View>
                  </View>
                )}
              )}
            </View>
          </View> : <View>
            <View flexDirection="row" alignItems="center" py="8px" px="12px" borderTopWidth={1} borderColor={color.palette.gray200} backgroundColor={color.palette.gray100}>
              <View flex={1}>
                <Text tx="autoFeederPondDetailScreen.date" preset="title4" colorScheme={colorScheme} />
              </View>
              <View flex={1} alignItems="center">
                <Text tx="autoFeederPondDetailScreen.quantity" preset="title4"
                      colorScheme={colorScheme} />
              </View>
              <View flex={1} alignItems="center">
                <Text tx="autoFeederPondDetailScreen.intervalShort" preset="title4"
                      colorScheme={colorScheme} />
              </View>
              <View flex={1} alignItems="flex-end">
                <Text tx="edit" preset="title4" colorScheme={colorScheme} />
              </View>
            </View>
            <Divider />
            <View>
              {nextFeeds.map((nextFeed, index) => (
                <View key={`${index}`} flexDirection="row" px="12px" py="8px" backgroundColor={index %2 === 0 ? color.palette.white : color.palette.gray100 }>
                  <View flex={1} justifyContent="center">
                    <Text text={moment(nextFeed.feed_at).format("DD MMM")} preset="body2" colorScheme={colorScheme} />
                  </View>
                  <View flex={1} flexDirection="row" alignItems="center" justifyContent="center">
                    {loadingAutoFeederPond
                      ? <ActivityIndicator size={18} color={color.primary} style={INDICATOR} />
                      : <Text text={`${nextFeed.quantity && numeral(nextFeed.quantity).format("0,0.[00]") || "-"} kg`}
                          preset="body2" colorScheme={colorScheme} mr="5px" />
                    }
                  </View>
                  <View flex={1} alignItems="center">
                    <Text
                      text={`${nextFeed.interval && numeral(nextFeed.interval).format("0,0.[00]") || "-"} ${translate("autoFeederPondDetailScreen.intervalUnitShort")}`}
                      marginRight="11px" preset="body2" colorScheme={colorScheme} />
                  </View>
                  <View flex={1} alignItems="flex-end">
                    <SettingItem
                      isDisabled={isFeedChanged || loadingAutoFeederPond}
                      type="action"
                      colorScheme={colorScheme}
                      onPress={() => onPressEdit(nextFeed.feed_at)}
                    >
                      <MaterialCommunityIcons name="pencil" color={color.primary} size={16} />
                    </SettingItem>
                  </View>
                </View>
              ))}
            </View>
          </View>}
        </View>
  )
}));

// const TextLastOnAt: FC<{ colorScheme?: "light" | "dark", lastOnAt?: string, startTime?: string, interval?: number, show?: "previous"|"next" }> = observer(function({ colorScheme, lastOnAt, startTime, interval, show = 'previous' }) {
//   const [ previousOnAt, setPreviousOnAt ] = useState<Moment>(null)
//   const [ nextOnAt, setNextOnAt ] = useState<Moment>(null)
//
//   const lastOnAtUsed = useMemo(() => {
//     if (lastOnAt && moment.utc(lastOnAt).local().isSame(undefined, 'day')) {
//       return lastOnAt;
//     } else {
//       return moment(startTime, 'HH:mm:ss').utc().format('YYYY-MM-DD HH:mm:ss');
//     }
//   }, [lastOnAt, startTime])
//
//   useEffect(() => {
//     const id = setInterval(() => {
//       if (!interval) {
//         setPreviousOnAt(null);
//         setNextOnAt(null);
//         return
//       }
//       const intervalSeconds = numeral(numeral(interval).format('0,0.[00]')).value() * 60;
//       const lastOnAtMoment = moment.utc(lastOnAtUsed).local()
//       let previousOnAt;
//       do {
//         previousOnAt = lastOnAtMoment.clone();
//         lastOnAtMoment.add(intervalSeconds, 'seconds');
//       } while (lastOnAtMoment.isBefore());
//       const nextOnAt = lastOnAtMoment.clone();
//       setPreviousOnAt(previousOnAt);
//       setNextOnAt(nextOnAt);
//     }, 1000 );
//     return () => {
//       clearInterval(id)
//     }
//   }, [interval, lastOnAt, lastOnAtUsed])
//
//   const text = show === 'previous'
//     ? `${previousOnAt && `${previousOnAt.format('HH:mm:ss')}\n+${moment.utc(moment().diff(previousOnAt)).format('mm:ss')}` || '-'}`
//     : `${nextOnAt && `${nextOnAt.format('HH:mm:ss')}\n-${moment.utc(nextOnAt.diff(moment())).format('mm:ss')}` || '-'}`;
//
//   return <Text
//     text={text}
//     preset="body3"
//     textAlign="right"
//     colorScheme={colorScheme}
//   />;
// })

export const AutoFeederPondDetailScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function AutoFeederPondDetailScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), navigation } = props

  const { isOpen, onClose, onOpen } = useDisclose()
  const isFocused = useIsFocused();

  const [ initialized, setInitialized ] = useState<boolean>(false)
  const [ uploading, setUploading ] = useState<boolean>(false)
  const [ isUploadSuccess, setIsUploadSuccess ] = useState<boolean>(false)
  const [ isShowCalibration, setIsShowCalibration ] = useState<boolean>(true)
  const [ progress, setProgress ] = useState<number>()

  const scrollViewRef = useRef<ScrollView>(null)

  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()
  const originalState = model && model.getAttributes().state;
  const state = useMemo(() => (
    originalState ? model.getAttribute('state') : undefined
  ), [model, originalState])

  // Pull in one of our MST stores
  const {
    appStore: { farmStore: { pondStore, pondStore: { autoFeederPond, loadingAutoFeederPond } } },
    autoFeederPondStore: modelStore,
    autoFeederPondStore: { pushIdQueue, needRefresh, loading: loadingAutoFeeder },
    userStore: { user }
  } = useStores()

  useEffect(() => {
    const callback = () => {
      if (model && model.isConnected) model.disconnect().catch()
    }
    navigation.addListener("beforeRemove", callback)
    return () => {
      navigation.removeListener("beforeRemove", callback)
    }
  }, [model, navigation])

  useEffect(() => {
    const instance = InteractionManager.runAfterInteractions(() => {
      if (!model) return
      if (!model.state?.asto_wizard_finish) {
        goBack('Home.Stack')
        navigate("Home.Stack", "Home.AutoFeederPond.Wizard.Connection.Setup")
      }
      else {
        modelStore.init()
      }
    })

    return instance.cancel
  }, [model, modelStore, navigation, user])

  useEffect(() => {
    const instance = InteractionManager.runAfterInteractions(() => {
      if (!model) setModel(AutoFeederPond.hydrate({ ...autoFeederPond }))
      delay(100).then(() => {
        setInitialized(true)
      })
    })

    return instance.cancel
  }, [autoFeederPond, model, navigation, setModel])

  useEffect(() => {
    const instance = InteractionManager.runAfterInteractions(() => {
      if (needRefresh) {
        pondStore.getAutoFeederPonds(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
          model.fill({ ...pondStore.autoFeederPond })
          model.syncOriginal()
          setModel(model)
        })
      }
    })
    return instance.cancel
  }, [pondStore, modelStore, needRefresh, setModel, model])

  // useEffect(() => {
  //   setIsShowCalibration(() => {
  //     if (!model?.state?.mode) return false;
  //     const feed = (model.state?.feeds || []).find((f: any) => f.feed_at === moment().format('YYYY-MM-DD') && f.quantity > 0)
  //     if (!feed) return true
  //     if (model.state?.mode === 'normal') {
  //       const feedPerDay = _.maxBy<any>(feed?.feedsPerDay || [], 'time')
  //       if (!feedPerDay) return true
  //       return model.state?.mode === 'normal' && moment(feedPerDay.time, 'HH:mm:ss').isBefore(moment())
  //     }
  //     return model.state?.mode === 'continuous' && moment(model.state?.endTime, 'HH:mm:ss').isBefore(moment())
  //   })
  // }, [model, model?.state?.endTime, model?.state?.mode])

  const originalFeeds = useMemo(() => [0, 1, 2, 3, 4, 5, 6].map((day) => {
    const date = moment().startOf('day').add(day, 'days').format('YYYY-MM-DD');
    const feed = ((model && state?.feeds || []).find((f) => f.feed_at === date) || {})
    const feedsPerDay = [];
    const timesPerDay = model && state?.timesPerDay || 1;
    const intervalPeriod = model && state?.intervalPeriod || 60;
    const onTime = model && state?.onTime || 1;
    const debit = model && state?.debit || 350;
    for (let i = 0; i < timesPerDay; i++) {
      const feedPerDay = ((model && feed?.feedsPerDay || [])[i] || {});
      feedsPerDay[i] = transformFeedPerDay({
        time: null,
        percent: 100 / timesPerDay,
        ...feedPerDay,
        intervalPeriod,
        onTime,
        debit,
      })
    }
    feed.feedsPerDay = _.sortBy(feedsPerDay, 'time');

    return transformFeed({
      ...(model && state || {}),
      ...feed,
      feed_at: date
    })
  }), [model, state])

  const originalFeedsPerDay = useMemo(() => {
    const feedsPerDay = [];
    const timesPerDay = model && state?.timesPerDay || 1;
    const intervalPeriod = model && state?.intervalPeriod || 60;
    const onTime = model && state?.onTime || 1;
    const debit = model && state?.debit || 350;
    for (let i = 0; i < timesPerDay; i++) {
      const feedPerDay = ((model && state?.feedsPerDay || {})[i] || {});
      feedsPerDay[i] = transformFeedPerDay({
        time: null,
        percent: 100 / timesPerDay,
        ...feedPerDay,
        intervalPeriod,
        onTime,
        debit,
      })
    }
    return _.sortBy(feedsPerDay, 'time');
  }, [model, state])

  const [ feedState, dispatchFeedState ] = useReducer(feedStateReducer, {
    modified: false,
    originalTimesPerDay: autoFeederPond?.state?.timesPerDay || 1,
    timesPerDay: autoFeederPond?.state?.timesPerDay || 1,
    originalFeeds: [...originalFeeds],
    feeds: [...originalFeeds],
    originalFeedsPerDay: [...originalFeedsPerDay],
  })

  useEffect(() => {
    const instance = InteractionManager.runAfterInteractions(() => {
      if (model) dispatchFeedState({
        type: "reset",
        payload: { originalTimesPerDay: model.state?.timesPerDay, originalFeeds, originalFeedsPerDay }
      })
    })

    return instance.cancel
  }, [model, originalFeeds, originalFeedsPerDay])

  useEffect(() => {
    if (loadingAutoFeederPond) activateKeepAwake()
    else deactivateKeepAwake();
  }, [loadingAutoFeederPond])

  const ConnectModal = useCallback(() => {
    return <ConnectingModal isOpen={isOpen} onClose={onClose} />
  }, [isOpen, onClose])

  const setUploadSuccess = useCallback(async() => {
    setIsUploadSuccess(true)
    await delay(2)
    setIsUploadSuccess(false)
  }, [])

  const BottomFloatingButton = useCallback(() => {
    return !model?.isConnected
      ? <AutoFeederPondDisconnected onConnect={onOpen} />
      : <UploadButton
          setUploading={(state) => setUploading(state)}
          setProgress={(number) => setProgress(number)}
          setUploadSucces={setUploadSuccess}
          uploading={uploading}
          colorScheme={colorScheme}
        />
  }, [colorScheme, model?.isConnected, onOpen, setUploadSuccess, uploading])

  const UploadSuccessToast = useCallback(() => isUploadSuccess && <UploadSuccess />, [isUploadSuccess])

  const setHideCalibration = useCallback(() => {
    setIsShowCalibration(false)
  }, [])

  if (!model) return null

  return (<FeedStateContext.Provider value={{ state: feedState, dispatch: dispatchFeedState }}>
    {/* <FeedWarning /> */}
    <UploadWarning />
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === 'light' ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      scrollViewRef={scrollViewRef}
    >
      <TitleSection colorScheme={colorScheme} scrollViewRef={scrollViewRef} onShowConnectModal={onOpen} />
      <DetailSection colorScheme={colorScheme} scrollViewRef={scrollViewRef} uploading={uploading} progress={progress} />
      <View height={1} backgroundColor={`gray.${colorScheme === 'light' ? 200 : 800}`} marginTop="8px" />
      {isShowCalibration && <>
        <CalibrationInfo model={model} colorScheme={colorScheme} onClose={setHideCalibration} />
        <View height={1} backgroundColor={`gray.${colorScheme === 'light' ? 200 : 800}`} marginBottom="8px" />
      </>}
      <TodaySection colorScheme={colorScheme} scrollViewRef={scrollViewRef} />
      <WalkthroughElement
        id="asto-next-day-feed"
        scrollViewRef={scrollViewRef}
        scrollOffset={250}
      >
        <NextDaySection colorScheme={colorScheme} scrollViewRef={scrollViewRef} />
      </WalkthroughElement>
      <Modal isOpen={(pushIdQueue[model.id] && pushIdQueue[model.id] === 'deleting') || needRefresh || loadingAutoFeeder}>
        {/* <Spinner color={color.palette.white} /> */}
        <View
          width="72px" height="72px"
          alignItems="center"
          justifyContent="center"
          rounded="48px"
          backgroundColor="rgba(255, 255, 255, 0.75)"
        >
          <JalaLoading />
        </View>
        {/* <Text tx="Please Wait" colorScheme="dark" /> */}
      </Modal>
      <ConnectModal />
    </Screen>
    {/* <FloatingAction
      actions={actions}
      distanceToEdge={16}
      overlayColor="rgba(27, 119, 223, 0.5)"
      onPressItem={name => {
        console.log(`selected button: ${name}`);
        onPressActionButton(name)
      }}
    /> */}
    <BottomFloatingButton />
    <ProgressDialog
      isOpen={uploading}
      colorScheme={colorScheme}
      title={translate("uploading")}
      progressValue={progress}
    />
    <UploadSuccessToast />
    {initialized && isFocused && <OnboardAstoModal colorScheme={colorScheme} scrollViewRef={scrollViewRef} />}
  </FeedStateContext.Provider>)
})
